import React from 'react';
import './Card.css';

/**
 * AURORA CARD PRIMITIVE
 * 
 * A token-based card component for content containers
 * All styling comes from tokens.css
 * 
 * Variants: 'default', 'elevated', 'outline', 'ghost'
 * Padding: 'none', 'sm', 'md', 'lg'
 * 
 * @example
 * ```jsx
 * <Card variant="elevated" padding="md">
 *   <Card.Header>
 *     <Card.Title>Title</Card.Title>
 *   </Card.Header>
 *   <Card.Body>Content here</Card.Body>
 *   <Card.Footer>Footer</Card.Footer>
 * </Card>
 * ```
 */

const Card = React.forwardRef(({
  children,
  variant = 'default',
  padding = 'md',
  hoverable = false,
  className = '',
  ...props
}, ref) => {
  const classes = [
    'aurora-card',
    `aurora-card--${variant}`,
    `aurora-card--padding-${padding}`,
    hoverable && 'aurora-card--hoverable',
    className
  ].filter(Boolean).join(' ');

  return (
    <div ref={ref} className={classes} {...props}>
      {children}
    </div>
  );
});

Card.displayName = 'Card';

/* ============================================
   SUB-COMPONENTS
   ============================================ */

const CardHeader = ({ children, className = '', ...props }) => (
  <div className={`aurora-card-header ${className}`} {...props}>
    {children}
  </div>
);

const CardTitle = ({ children, className = '', as: Component = 'h3', ...props }) => (
  <Component className={`aurora-card-title ${className}`} {...props}>
    {children}
  </Component>
);

const CardDescription = ({ children, className = '', ...props }) => (
  <p className={`aurora-card-description ${className}`} {...props}>
    {children}
  </p>
);

const CardBody = ({ children, className = '', ...props }) => (
  <div className={`aurora-card-body ${className}`} {...props}>
    {children}
  </div>
);

const CardFooter = ({ children, className = '', ...props }) => (
  <div className={`aurora-card-footer ${className}`} {...props}>
    {children}
  </div>
);

const CardImage = ({ src, alt, className = '', ...props }) => (
  <div className={`aurora-card-image ${className}`} {...props}>
    <img src={src} alt={alt} />
  </div>
);

Card.Header = CardHeader;
Card.Title = CardTitle;
Card.Description = CardDescription;
Card.Body = CardBody;
Card.Footer = CardFooter;
Card.Image = CardImage;

export default Card;
